import React, { useRef, useEffect } from 'react';
import L from 'leaflet';
import { tileUrl } from '../theme';

const fmt = (v) => {
  if (v === null || v === undefined) return '—';
  if (v >= 1e9) return `R${(v/1e9).toFixed(1)}bn`;
  if (v >= 1e6) return `R${(v/1e6).toFixed(1)}m`;
  if (v >= 1e3) return `R${(v/1e3).toFixed(0)}k`;
  return `R${v.toFixed(0)}`;
};

const fmtSqm = (v) => v ? `${v.toLocaleString()} sqm` : '—';

const VERDICT_COLORS = {
  green: { bg: '#1a7a4a', text: 'white' },
  amber: { bg: '#b07820', text: 'white' },
  red:   { bg: '#b03020', text: 'white' },
};

const CENTRE_TYPE_LABELS = {
  super_regional: 'Super Regional',
  regional: 'Regional',
  small_regional: 'Small Regional',
  community: 'Community',
  neighbourhood: 'Neighbourhood',
};

export default function FeasibilityReportPanel({ result, request, location, onBack, isMobile }) {
  const reportRef = useRef(null);
  if (!result) return null;

  try {
    return <FeasibilityReportContent result={result} request={request} location={location} onBack={onBack} isMobile={isMobile} reportRef={reportRef} />;
  } catch (err) {
    console.error('FeasibilityReportPanel render error:', err);
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <div style={{ fontSize: 16, color: 'var(--red)', marginBottom: 12 }}>Report render error</div>
        <pre style={{ fontSize: 11, color: 'var(--muted)', textAlign: 'left', maxWidth: 600, margin: '0 auto', overflow: 'auto' }}>{err.message}</pre>
        <button onClick={onBack} style={{ marginTop: 20, padding: '10px 20px', background: 'var(--ink)', color: 'var(--cream)', border: 'none', borderRadius: 6, cursor: 'pointer' }}>← Back</button>
      </div>
    );
  }
}

function FeasibilityReportContent({ result, request, location, onBack, isMobile, reportRef }) {
  const { total_score = 0, verdict = '', verdict_color = 'amber', dimensions = [], rep = {}, feasibility, saturation, confidence } = result;
  const vc = VERDICT_COLORS[verdict_color] || VERDICT_COLORS.amber;
  const f = feasibility || {};
  const tenantMix = f.tenant_mix || [];
  const centres = (f.competing_centres || []).map(c => ({
    ...c,
    gla_sqm: c.gla_sqm || 0,
    tenant_categories: c.tenant_categories || {},
    lat: c.lat || null,
    lon: c.lon || null,
  }));
  const totalGla = f.recommended_gla_sqm || 0;
  const maxGla = tenantMix.length > 0 ? Math.max(...tenantMix.map(t => t.supportable_gla_sqm || 0), 1) : 1;

  const scoreColor = (s) => {
    if (s >= 80) return 'var(--s-green)';
    if (s >= 65) return 'var(--s-amber)';
    return 'var(--s-red)';
  };

  return (
    <div className="sq-dark-doc" style={styles.overlay} ref={reportRef}>

      {/* Top bar */}
      <div style={{ ...styles.topBar, ...(isMobile ? { padding: '10px 16px' } : {}) }} className="no-print">
        <button onClick={onBack} style={styles.backBtn}>← New Assessment</button>
        <div style={{ ...(isMobile ? { display: 'none' } : styles.topMeta) }}>
          <span style={styles.reportId}>SC-{Date.now().toString().slice(-6)}</span>
          <span style={styles.reportDate}>{new Date().toLocaleDateString('en-ZA', { day:'2-digit', month:'short', year:'numeric' })}</span>
        </div>
        <button onClick={() => {
          const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
          if (result.report_id) { window.open(`${API_BASE}/reports/${result.report_id}/pdf`, '_blank'); }
          else { window.print(); }
        }} style={styles.printBtn}>Download PDF</button>
      </div>

      <div style={{ ...styles.body, ...(isMobile ? { padding: '16px 16px 32px' } : {}) }}>

        {/* Header */}
        <div style={{ ...styles.siteHeader, ...(isMobile ? { flexDirection: 'column', gap: 16 } : {}) }}>
          <div>
            <div style={styles.productLabel}>Shopping Centre Feasibility Study</div>
            <div style={styles.siteName}>
              {location ? `${location.lat.toFixed(4)}° S, ${location.lon.toFixed(4)}° E` : 'Demo Site'}
            </div>
            <div style={styles.tagRow}>
              <span style={styles.tag}>{result.city_tier?.replace('_', ' ')}</span>
              <span style={styles.tag}>{result.dominant_lsm}</span>
              <span style={styles.tag}>{request?.radius_km || 5}km catchment</span>
            </div>
          </div>
          <div style={styles.headerRight}>
            <div style={{ ...styles.verdictBadge, background: vc.bg, color: vc.text }}>
              <div style={styles.verdictScore}>{total_score}</div>
              <div style={styles.verdictLabel}>{verdict}</div>
            </div>
          </div>
        </div>

        {/* GLA Recommendation */}
        <div style={styles.glaCard}>
          <div style={styles.glaHeader}>
            <div>
              <div style={styles.glaLabel}>Unmet GLA Demand</div>
              <div style={styles.glaValue}>{totalGla.toLocaleString()} sqm</div>
            </div>
            <div style={styles.centreTypeBadge}>
              {CENTRE_TYPE_LABELS[f.centre_type] || f.centre_type}
            </div>
          </div>
          <div style={styles.glaSummary}>
            <SummaryItem label="Total Catchment Spend" value={fmt(f.total_catchment_spend)} />
            <SummaryItem label="Existing Supply" value={fmt(f.total_existing_supply)} color="var(--red)" />
            <SummaryItem label="Unmet Demand" value={fmt(f.total_unmet_demand)} color="var(--green)" />
          </div>
        </div>

        {/* Tenant Mix */}
        <div style={styles.card}>
          <div style={styles.cardTitle}>Tenant Mix — GLA Demand by Category</div>
          <div style={styles.tenantList}>
            {tenantMix.map((t, i) => (
              <div key={i} style={styles.tenantRow}>
                <div style={styles.tenantInfo}>
                  <div style={styles.tenantName}>{t.category}</div>
                  <div style={styles.tenantMeta}>
                    {fmt(t.total_demand)} demand · {fmt(t.existing_supply)} supply · {fmt(t.unmet_demand)} unmet
                  </div>
                </div>
                <div style={styles.tenantBarWrap}>
                  <div style={styles.tenantBar}>
                    <div style={{
                      ...styles.tenantBarFill,
                      width: `${(t.supportable_gla_sqm / maxGla) * 100}%`,
                      background: t.unmet_demand > 0 ? 'var(--green)' : 'var(--rule)',
                    }} />
                  </div>
                </div>
                <div style={styles.tenantGla}>
                  <div style={styles.tenantGlaValue}>{t.supportable_gla_sqm?.toLocaleString()}</div>
                  <div style={styles.tenantGlaUnit}>sqm</div>
                </div>
                <div style={styles.tenantPct}>{t.pct}%</div>
              </div>
            ))}
          </div>
          <div style={styles.tenantTotal}>
            <span>Total Unmet GLA</span>
            <span style={{ fontWeight: 700 }}>{fmtSqm(totalGla)}</span>
          </div>
        </div>

        {/* Two column: (Dimensions + Catchment) | Competing Centres */}
        <div style={{ ...styles.twoCol, ...(isMobile ? { gridTemplateColumns: '1fr' } : {}) }}>

          {/* Left column */}
          <div>
            {/* Dimensions */}
            <div style={styles.card}>
              <div style={styles.cardTitle}>Site Scoring — {total_score}/100</div>
              <div style={styles.dimensions}>
                {dimensions?.map((d, i) => {
                  const isNA = d.score === null || d.score === undefined;
                  return (
                    <div key={i} style={{ ...styles.dimRow, opacity: isNA ? 0.5 : 1 }}>
                      <div style={styles.dimName}>{d.name}</div>
                      <div style={styles.dimBar}>
                        {!isNA && (
                          <div style={{ ...styles.dimFill, width: `${d.score}%`, background: scoreColor(d.score) }} />
                        )}
                      </div>
                      <div style={{ ...styles.dimScore, color: isNA ? 'var(--muted)' : scoreColor(d.score) }}>
                        {isNA ? 'N/A' : Math.round(d.score)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Catchment Profile */}
            <div style={styles.card}>
              <div style={styles.cardTitle}>Catchment Profile</div>
              <div style={styles.profileGridCompact}>
                <ProfileItem label="Total Households" value={(rep?.total_hh || 0).toLocaleString()} />
                <ProfileItem label="Dominant LSM" value={result.dominant_lsm || '—'} />
                <ProfileItem label="Catchment Spend" value={fmt(f.total_catchment_spend)} />
                <ProfileItem label="Catchment Radius" value={`${request?.radius_km || 5}km`} />
              </div>
              <div style={styles.lsmRow}>
                {['LSM9-10', 'LSM7-8', 'LSM5-6', 'LSM4-5', 'LSM3-4'].map(tier => {
                  const hh = rep?.[`hh_${tier.toLowerCase().replace('-', '_')}`] || 0;
                  const total = rep?.total_hh || 1;
                  const pct = (hh / total * 100).toFixed(0);
                  return (
                    <div key={tier} style={styles.lsmItem}>
                      <div style={styles.lsmBar}>
                        <div style={{ ...styles.lsmFill, height: `${pct}%` }} />
                      </div>
                      <div style={styles.lsmLabel}>{tier}</div>
                      <div style={styles.lsmPct}>{pct}%</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Competing Centres */}
          <div style={styles.card}>
            <div style={styles.cardTitle}>Competing Centres — {centres.length} within {request?.radius_km || 5}km</div>
            {centres.length === 0 ? (
              <div style={styles.emptyText}>No competing shopping centres found in catchment.</div>
            ) : (
              <>
                <CentreMap location={location} centres={centres} radiusKm={request?.radius_km || 5} />
                <div style={styles.centreList}>
                  {centres.map((c, i) => (
                    <div key={i} style={styles.centreRow}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                        <div style={styles.centreName}>{c.name}</div>
                        <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, color: 'var(--muted)' }}>
                          {c.dist_km}km
                        </div>
                      </div>
                      <div style={styles.centreMeta}>
                        <span>{fmtSqm(c.gla_sqm)}</span>
                        <span style={styles.dot}>·</span>
                        <span style={styles.centreType}>{CENTRE_TYPE_LABELS[c.centre_type] || c.centre_type}</span>
                        <span style={styles.dot}>·</span>
                        <span>{c.poi_count} POIs</span>
                      </div>
                      {c.tenant_categories && Object.keys(c.tenant_categories).length > 0 && (
                        <TenantBreakdownBar categories={c.tenant_categories} />
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Network Ranking */}
        {result.network_ranking && (
          <div style={styles.card}>
            <div style={styles.cardTitle}>
              Network Ranking · #{result.network_ranking.score_rank} of {result.network_ranking.network_size} stores
            </div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
              <div style={{ textAlign: 'center', padding: '14px 20px', background: 'var(--cream)', borderRadius: 8 }}>
                <div style={{ fontFamily: "'News Cycle',serif", fontSize: 28, fontWeight: 700, color: scoreColor(total_score) }}>
                  Top {result.network_ranking.score_percentile}%
                </div>
                <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 9, color: 'var(--muted)', textTransform: 'uppercase', marginTop: 4 }}>Percentile</div>
              </div>
              <div style={{ display: 'flex', gap: 8, flex: 1, flexWrap: 'wrap' }}>
                {[
                  { label: 'This Site', val: total_score, color: scoreColor(total_score) },
                  { label: 'Network Avg', val: result.network_ranking.avg_score },
                  { label: 'Best', val: result.network_ranking.best_score, color: 'var(--s-green)' },
                  { label: 'Weakest', val: result.network_ranking.worst_score, color: 'var(--s-red)' },
                ].map(s => (
                  <div key={s.label} style={{ background: 'var(--cream)', borderRadius: 6, padding: '10px 12px', textAlign: 'center', flex: '1 1 60px' }}>
                    <div style={{ fontFamily: "'News Cycle',serif", fontSize: 18, fontWeight: 700, color: s.color || 'var(--ink)' }}>{s.val}</div>
                    <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 8, color: 'var(--muted)', textTransform: 'uppercase' }}>{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Anchor Recommendation */}
        <div style={{ ...styles.card, background: 'var(--s-solid)', borderColor: 'var(--s-line)', color: 'var(--s-text)' }}>
          <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 10, color: 'var(--s-w40)', letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 8 }}>
            Recommendation
          </div>
          <div style={{ fontFamily: "'News Cycle',serif", fontSize: 20, color: '#fff', marginBottom: 10 }}>
            {totalGla > 0
              ? `${CENTRE_TYPE_LABELS[f.centre_type] || 'Shopping'} Centre — ${fmtSqm(totalGla)}`
              : 'Insufficient unmet demand for a new centre'}
          </div>
          <div style={{ fontSize: 13, color: 'var(--s-w65)', lineHeight: 1.7, marginBottom: 12 }}>
            This site scores {total_score}/100. The catchment delivers {fmt(f.total_catchment_spend)}/mo in total retail spend.
            Existing centres absorb {fmt(f.total_existing_supply)}/mo, leaving {fmt(f.total_unmet_demand)}/mo in unmet demand.
            {totalGla > 0 && ` This supports a ${fmtSqm(totalGla)} ${CENTRE_TYPE_LABELS[f.centre_type]?.toLowerCase() || ''} centre.`}
          </div>
          {f.anchor_recommendation && (
            <div style={{ fontSize: 13, color: 'var(--s-w65)', lineHeight: 1.7, padding: '10px 12px', background: 'var(--s-w06)', borderRadius: 6 }}>
              <strong style={{ color: '#fff' }}>Anchor:</strong> {f.anchor_recommendation}
            </div>
          )}
          {saturation && (
            <div style={{ marginTop: 12, fontFamily: "'DM Mono',monospace", fontSize: 11, color: 'var(--s-w40)' }}>
              Market: {saturation.verdict} · {centres.length} competing centre{centres.length !== 1 ? 's' : ''} · {(saturation.existing_gla_total || 0).toLocaleString()} sqm existing GLA
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ ...styles.footer, ...(isMobile ? { flexDirection: 'column', gap: 8, textAlign: 'center' } : {}) }}>
          <div>SiteIQ Shopping Centre Feasibility · Confidential · {new Date().getFullYear()}</div>
          <div style={{ fontFamily: "'News Cycle',serif", color: 'var(--rule)', fontSize: 14, letterSpacing: 3 }}>SITEIQ</div>
          <div>Data: Microsoft Footprints · ESRI · IES 2022/23 · POI Database · SACSC Benchmarks</div>
        </div>

      </div>
    </div>
  );
}

const CAT_COLORS = {
  Grocery: '#1a7a4a', QSR: '#c85020', Fashion: '#1b3bb0', Pharmacy: '#7a4ab0',
  Dining: '#b07820', Coffee: '#6b4c3b', Fuel: '#555', Other: '#aaa',
};

function CentreMap({ location, centres, radiusKm }) {
  const mapRef = useRef(null);
  const mapObj = useRef(null);

  useEffect(() => {
    if (!mapRef.current || !location) return;
    if (mapObj.current) { mapObj.current.remove(); mapObj.current = null; }
    try {
      const map = L.map(mapRef.current, {
        center: [location.lat, location.lon],
        zoomControl: true,
        attributionControl: false,
      });
      mapObj.current = map;

      const tiles = L.tileLayer(
        tileUrl(),
        { subdomains: 'abcd', maxZoom: 19 }
      ).addTo(map);
      const onTheme = () => tiles.setUrl(tileUrl());
      window.addEventListener('themechange', onTheme);
      map.on('unload', () => window.removeEventListener('themechange', onTheme));

      const circle = L.circle([location.lat, location.lon], {
        radius: (radiusKm || 5) * 1000, color: '#ff6a3d', fillColor: '#ff6a3d',
        fillOpacity: 0.05, weight: 1.3, dashArray: '6 6',
      }).addTo(map);
      map.fitBounds(circle.getBounds().pad(0.1));

      const pinIcon = L.divIcon({
        html: '<div style="width:14px;height:14px;background:#ff6a3d;border:2px solid var(--s-solid);border-radius:50%;box-shadow:0 0 8px rgba(0,0,0,0.6)"></div>',
        iconSize: [14, 14], iconAnchor: [7, 7], className: '',
      });
      L.marker([location.lat, location.lon], { icon: pinIcon }).addTo(map);

      (centres || []).forEach(c => {
        if (c.lat == null || c.lon == null) return;
        const icon = L.divIcon({
          html: '<div style="width:11px;height:11px;background:#7db8ff;border:2px solid var(--s-solid);border-radius:3px;box-shadow:0 0 6px rgba(0,0,0,0.6)"></div>',
          iconSize: [10, 10], iconAnchor: [5, 5], className: '',
        });
        L.marker([c.lat, c.lon], { icon }).addTo(map).bindPopup(
          `<div style="font-family:'Work Sans',sans-serif;font-size:12px"><strong>${c.name || 'Centre'}</strong><br>${(c.gla_sqm||0).toLocaleString()} sqm · ${c.dist_km || '?'}km</div>`
        );
      });
    } catch (err) {
      console.error('CentreMap error:', err);
    }

    return () => { if (mapObj.current) { mapObj.current.remove(); mapObj.current = null; } };
  }, [location, centres, radiusKm]);

  return <div ref={mapRef} style={styles.miniMap} />;
}

function TenantBreakdownBar({ categories }) {
  const total = Object.values(categories).reduce((s, v) => s + v, 0);
  if (total === 0) return null;
  const entries = Object.entries(categories).sort((a, b) => b[1] - a[1]);
  return (
    <div style={{ marginTop: 6 }}>
      <div style={styles.breakdownBar}>
        {entries.map(([cat, count]) => (
          <div key={cat} style={{
            width: `${(count / total * 100)}%`,
            height: '100%',
            background: CAT_COLORS[cat] || '#aaa',
            minWidth: 3,
          }} title={`${cat}: ${count} (${(count/total*100).toFixed(0)}%)`} />
        ))}
      </div>
      <div style={styles.breakdownLegend}>
        {entries.map(([cat, count]) => (
          <div key={cat} style={styles.breakdownItem}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: CAT_COLORS[cat] || '#aaa', flexShrink: 0 }} />
            <span>{cat} {(count/total*100).toFixed(0)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SummaryItem({ label, value, color }) {
  return (
    <div style={styles.summaryItem}>
      <div style={styles.summaryLabel}>{label}</div>
      <div style={{ ...styles.summaryValue, ...(color ? { color } : {}) }}>{value}</div>
    </div>
  );
}

function ProfileItem({ label, value }) {
  return (
    <div style={styles.profileItem}>
      <div style={styles.profileLabel}>{label}</div>
      <div style={styles.profileVal}>{value}</div>
    </div>
  );
}

const styles = {
  overlay: { position: 'fixed', inset: 0, background: 'var(--cream)', overflowY: 'auto', zIndex: 2000, animation: 'fadeUp 0.3s ease' },
  topBar: { background: 'var(--s-solid)', padding: '12px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--rule)', position: 'sticky', top: 0, zIndex: 10 },
  backBtn: { background: 'transparent', color: 'var(--s-w60)', border: '1px solid var(--s-w14)', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontSize: 12, fontFamily: "'Work Sans',sans-serif" },
  topMeta: { display: 'flex', gap: 16, alignItems: 'center' },
  reportId: { fontFamily: "'DM Mono',monospace", fontSize: 11, color: 'var(--s-w50)' },
  reportDate: { fontFamily: "'DM Mono',monospace", fontSize: 11, color: 'var(--s-w40)' },
  printBtn: { background: 'var(--s-accent)', color: '#14110e', border: 'none', borderRadius: 8, padding: '8px 18px', cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: "'Work Sans',sans-serif" },
  body: { maxWidth: 1100, margin: '0 auto', padding: '28px 32px 48px' },
  siteHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, paddingBottom: 20, borderBottom: '1px solid var(--rule)' },
  productLabel: { fontFamily: "'DM Mono',monospace", fontSize: 10, letterSpacing: '1.2px', textTransform: 'uppercase', color: 'var(--s-accent)', marginBottom: 6 },
  siteName: { fontFamily: "'News Cycle',serif", fontSize: 22, fontWeight: 700, marginBottom: 10 },
  tagRow: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  tag: { display: 'inline-block', padding: '3px 10px', background: 'var(--s-w06)', border: '1px solid var(--rule)', borderRadius: 4, fontSize: 10, fontFamily: "'DM Mono',monospace", letterSpacing: '0.8px', textTransform: 'uppercase', color: 'var(--muted)' },
  headerRight: { display: 'flex', gap: 10, alignItems: 'stretch', flexShrink: 0 },
  verdictBadge: { padding: '16px 24px', borderRadius: 6, textAlign: 'center', minWidth: 110, flexShrink: 0 },
  verdictScore: { fontFamily: "'News Cycle',serif", fontSize: 36, fontWeight: 700, lineHeight: 1 },
  verdictLabel: { fontSize: 11, marginTop: 4, letterSpacing: '0.8px', fontFamily: "'DM Mono',monospace", textTransform: 'uppercase' },

  glaCard: { background: 'var(--white)', border: '1.5px solid var(--s-accent)', borderRadius: 8, padding: 24, marginBottom: 20 },
  glaHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  glaLabel: { fontFamily: "'DM Mono',monospace", fontSize: 10, letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 4 },
  glaValue: { fontFamily: "'News Cycle',serif", fontSize: 36, fontWeight: 700, color: 'var(--ink)' },
  centreTypeBadge: { fontFamily: "'DM Mono',monospace", fontSize: 11, fontWeight: 600, letterSpacing: '0.8px', textTransform: 'uppercase', color: 'var(--s-accent)', background: 'rgba(255,106,61,0.1)', padding: '8px 14px', borderRadius: 6, border: '1px solid var(--s-accent)' },
  glaSummary: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 },
  summaryItem: { background: 'var(--cream)', borderRadius: 6, padding: '14px 16px' },
  summaryLabel: { fontFamily: "'DM Mono',monospace", fontSize: 10, letterSpacing: '0.8px', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 4 },
  summaryValue: { fontFamily: "'News Cycle',serif", fontSize: 22, fontWeight: 700, color: 'var(--ink)' },

  card: { background: 'var(--white)', border: '1px solid var(--rule)', borderRadius: 6, padding: 22, marginBottom: 16 },
  cardTitle: { fontFamily: "'DM Mono',monospace", fontSize: 10, fontWeight: 500, letterSpacing: '1.2px', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 14, paddingBottom: 10, borderBottom: '1px solid var(--rule)' },

  tenantList: { display: 'flex', flexDirection: 'column', gap: 10 },
  tenantRow: { display: 'grid', gridTemplateColumns: '1fr 1fr 80px 50px', alignItems: 'center', gap: 12 },
  tenantInfo: { minWidth: 0 },
  tenantName: { fontSize: 13, fontWeight: 600, color: 'var(--ink)' },
  tenantMeta: { fontFamily: "'DM Mono',monospace", fontSize: 10, color: 'var(--muted)', marginTop: 2 },
  tenantBarWrap: {},
  tenantBar: { height: 8, background: 'var(--rule)', borderRadius: 4, overflow: 'hidden' },
  tenantBarFill: { height: '100%', borderRadius: 4, transition: 'width 0.8s ease' },
  tenantGla: { textAlign: 'right' },
  tenantGlaValue: { fontFamily: "'News Cycle',serif", fontSize: 16, fontWeight: 700, color: 'var(--ink)' },
  tenantGlaUnit: { fontFamily: "'DM Mono',monospace", fontSize: 9, color: 'var(--muted)' },
  tenantPct: { fontFamily: "'DM Mono',monospace", fontSize: 12, color: 'var(--muted)', textAlign: 'right' },
  tenantTotal: { display: 'flex', justifyContent: 'space-between', paddingTop: 12, marginTop: 12, borderTop: '2px solid var(--ink)', fontFamily: "'DM Mono',monospace", fontSize: 12, color: 'var(--ink)' },

  twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 },

  dimensions: { display: 'flex', flexDirection: 'column', gap: 10 },
  dimRow: { display: 'grid', gridTemplateColumns: '120px 1fr 30px', alignItems: 'center', gap: 8 },
  dimName: { fontSize: 11, color: 'var(--muted)', fontFamily: "'DM Mono',monospace", textTransform: 'uppercase', letterSpacing: '0.4px' },
  dimBar: { height: 6, background: 'var(--rule)', borderRadius: 3, overflow: 'hidden' },
  dimFill: { height: '100%', borderRadius: 3, transition: 'width 1s ease' },
  dimScore: { fontFamily: "'DM Mono',monospace", fontSize: 12, fontWeight: 500, textAlign: 'right' },

  centreList: { display: 'flex', flexDirection: 'column', gap: 10 },
  centreRow: { padding: '12px', background: 'var(--cream)', borderRadius: 6 },
  centreName: { fontSize: 13, fontWeight: 600, color: 'var(--ink)' },
  centreMeta: { fontFamily: "'DM Mono',monospace", fontSize: 11, color: 'var(--muted)', marginTop: 4, display: 'flex', gap: 6 },
  dot: { color: 'var(--rule)' },
  centreType: { textTransform: 'capitalize' },
  miniMap: { width: '100%', height: 200, borderRadius: 6, marginBottom: 12, overflow: 'hidden', border: '1px solid var(--rule)' },
  breakdownBar: { display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', gap: 1 },
  breakdownLegend: { display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 },
  breakdownItem: { display: 'flex', alignItems: 'center', gap: 4, fontSize: 9, color: 'var(--muted)', fontFamily: "'DM Mono',monospace" },
  emptyText: { fontSize: 13, color: 'var(--muted)', textAlign: 'center', padding: '20px 0' },

  profileGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 },
  profileGridCompact: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 },
  profileItem: { padding: 12, background: 'var(--cream)', borderRadius: 4 },
  profileLabel: { fontFamily: "'DM Mono',monospace", fontSize: 10, color: 'var(--muted)', letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: 4 },
  profileVal: { fontFamily: "'News Cycle',serif", fontSize: 18, fontWeight: 700 },

  lsmRow: { display: 'flex', justifyContent: 'space-around', alignItems: 'flex-end', height: 80, gap: 8 },
  lsmItem: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 },
  lsmBar: { width: 24, height: 50, background: 'var(--rule)', borderRadius: 3, overflow: 'hidden', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' },
  lsmFill: { width: '100%', background: 'var(--s-accent)', borderRadius: 3, transition: 'height 0.8s ease' },
  lsmLabel: { fontFamily: "'DM Mono',monospace", fontSize: 9, color: 'var(--muted)' },
  lsmPct: { fontFamily: "'DM Mono',monospace", fontSize: 10, fontWeight: 600, color: 'var(--ink)' },

  footer: { marginTop: 32, paddingTop: 16, borderTop: '1px solid var(--rule)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, color: 'var(--muted)', fontFamily: "'DM Mono',monospace" },
};
